// Service Dependencies
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';

// App Config data
import { AppSettings } from '../../../app.config';

// Rxjs
import { lastValueFrom, Observable, throwError } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { PdfBase64RequestBody } from 'src/app/modules/acr/arc-clients/shared/client-cases-modals/generate-model-draft/models/PdfBase64RequestBody';
import { IPdfBase64Response, IPdfBase64ResponseType } from 'src/app/modules/acr/arc-clients/shared/client-cases-modals/generate-model-draft/models/IPdfBase64Response';
import { IUpdateSelfAssessmentPersonAndTaxPayerRepresentative } from './dtos/IUpdateSelfAssessmentPersonAndTaxPayerRepresentative';
import { buildModelDetailedResultPayload } from './dtos/model-detailed-result-payload.util';
import { buildModelDetailedResult151Payload } from './dtos/model-detailed-result-151-payload.util';
import { buildModelPeriodDates151Payload } from './dtos/model-period-dates-151-payload.util';
import { buildModelPeriodDatesPayload } from './dtos/model-period-dates-payload.util';
import { buildModelPeriodTypesPayload } from './dtos/model-period-types-payload.util';
import { buildModelPreparationPayload } from './dtos/model-preparation-payload.util';
import { buildModelPreparation030Payload } from './dtos/model-preparation-030-payload.util';
import { buildModelPreparation149Payload } from './dtos/model-preparation-149-payload.util';
import { buildModelPreparation151Payload } from './dtos/model-preparation-151-payload.util';
import { buildModelDetailedResultExpensesPayload } from './dtos/model-detailed-result-expenses-payload.util';
import { buildModelDomicilesInformationPayload } from './dtos/model-domiciles-information-payload.util';
import { buildModelPayerCapitalGainRealStatePayload } from './dtos/model-payer-capital-gain-real-state-payload.util';
import { buildModelResultDonationPayload } from './dtos/model-result-donation-payload.util';
import { buildGenerateFormsDraftPdfPayload, buildGenerateFormsPayload, GenerateDraftPdfInput, GenerateFormsDraftPdfPayload } from './dtos/generate-forms-payload.util';
import { buildGenerateForms030Payload } from './dtos/generate-forms-030-payload.util';
import { buildGenerateForms149Payload } from './dtos/generate-forms-149-payload.util';
import { buildGenerateForms151DraftPdfPayload } from './dtos/generate-forms-151-payload.util';


@Injectable()
export class NationalIncomeTaxReturnService {

  apiUrl: string;

  constructor(
    private http: HttpClient
  ) {
    this.apiUrl = AppSettings.API_PROD_ENDPOINT;
  }



  /*********** IDENTIFICATION DETAILS ***********/

  getSelfAssessmentPersonAndTaxPayerRepresentative(
    caseId: string = '',
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `self-assess-person-representative/` + caseId;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  createSelfAssessmentPersonAndTaxPayerRepresentative(
    item: any,
  ): Observable<any> {
    const body = JSON.stringify(item);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `self-assess-person-representative`

    return this.http.post(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  updateSelfAssessmentPersonAndTaxPayerRepresentative(
    itemId: string = '',
    item: IUpdateSelfAssessmentPersonAndTaxPayerRepresentative,
  ): Observable<any> {
    const body = JSON.stringify(item);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `self-assess-person-representative/` + itemId

    return this.http.put(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  deleteSelfAssessmentPersonAndTaxPayerRepresentative(
    itemId: string = '',
    caseId: string = '',
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `self-assess-person-representative/` + itemId + `/` + caseId

    return this.http.delete(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res),
        catchError(err => {
          console.error('Error: ' + err.error.code + ': ' +
            err.error.message + ' (' + err.status + ': ' +
            err.statusText + ')'
          );
          return err;
        })
      );
  }

  /*********** MODEL DOMICILE INFORMATION ***********/

  getModelDomicilesInformation(
    caseId: string = '',
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-domiciles-information/` + caseId;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  createModelDomicilesInformation(
    item: any,
  ): Observable<any> {
    const payload = buildModelDomicilesInformationPayload(item);
    const body = JSON.stringify(payload);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-domiciles-information`

    return this.http.post(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  updateModelDomicilesInformation(
    itemId: string = '',
    item: any,
  ): Observable<any> {
    const payload = buildModelDomicilesInformationPayload(item);
    const body = JSON.stringify(payload);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-domiciles-information/` + itemId

    return this.http.put(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  deleteModelDomicilesInformation(
    itemId: string = '',
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-domiciles-information/` + itemId

    return this.http.delete(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res),
        catchError(err => {
          console.error('Error: ' + err.error.code + ': ' +
            err.error.message + ' (' + err.status + ': ' +
            err.statusText + ')'
          );
          return err;
        })
      );
  }

  /*********** MODEL BANK ACCOUNT INFORMATION ***********/

  getModelBankAccountInformation(
    caseId: string = '',
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-bank-account-information/` + caseId;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  createModelBankAccountInformation(
    item: any,
  ): Observable<any> {
    const body = JSON.stringify(item);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-bank-account-information`

    return this.http.post(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  updateModelBankAccountInformation(
    itemId: string = '',
    item: any,
  ): Observable<any> {
    const body = JSON.stringify(item);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-bank-account-information/` + itemId

    return this.http.put(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  deleteModelBankAccountInformation(
    itemId: string = '',
    caseId: string = '',
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-bank-account-information/` + itemId + `/` + caseId;

    return this.http.delete(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res),
        catchError(err => {
          console.error('Error: ' + err.error.code + ': ' +
            err.error.message + ' (' + err.status + ': ' +
            err.statusText + ')'
          );
          return err;
        })
      );
  }


  /******************** 210 ********************/

  get210AssetSubtypeRentingHousesTable(
    incomeDetailsSummaryId: string = '',
    tableName: string = '',
    caseId: string = ''
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `getRentingHousesTable/` + incomeDetailsSummaryId + '/table/' + tableName + "/" + caseId;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  get210AssetSubtypeIRHousesTable(
    incomeDetailsSummaryId: string = '',
    tableName: string = '',
    caseId: string = ''
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `getIRHousesTable/` + incomeDetailsSummaryId + '/table/' + tableName + "/" + caseId;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  get210AssetSubtypeEconomicActivityTable(
    incomeDetailsSummaryId: string = '',
    caseId: string = ''
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `getEconomicActivityTable/` + incomeDetailsSummaryId + "/" + caseId;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  get210AssetSubtypeDividendsTable(
    incomeDetailsSummaryId: string = '',
    caseId: string = ''
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `getDividendsTable/` + incomeDetailsSummaryId + "/" + caseId;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  get210AssetSubtypeInterestsTable(
    incomeDetailsSummaryId: string = '',
    caseId: string = ''
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `getInterestsTable/` + incomeDetailsSummaryId + "/" + caseId;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  get210AssetSubtypeCanonsTable(
    incomeDetailsSummaryId: string = '',
    caseId: string = ''
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `getCanonsTable/` + incomeDetailsSummaryId + "/" + caseId;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  get210AssetSubtypeWorkPerformanceTable(
    incomeDetailsSummaryId: string = '',
    tableName: string = '',
    caseId: string = ''
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `getWorkPerformanceTable/` + incomeDetailsSummaryId + '/table/' + tableName + "/" + caseId;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  get210AssetSubtypePensionPlansTable(
    incomeDetailsSummaryId: string = '',
    tableName: string = '',
    caseId: string = ''
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `getPensionPlansTable/` + incomeDetailsSummaryId + '/table/' + tableName + "/" + caseId;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  get210AssetSubtypeCapitalGainStocksTable(
    incomeDetailsSummaryId: string = '',
    caseId: string = ''
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `getCapitalGainStocksTable/` + incomeDetailsSummaryId + "/" + caseId;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  get210AssetSubtypeCapitalGainInvestmentFundsTable(
    incomeDetailsSummaryId: string = '',
    caseId: string = ''
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `getCapitalGainInvestmentFundsTable/` + incomeDetailsSummaryId + "/" + caseId;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  get210AssetSubtypeCapitalGainRealStateTable(
    incomeDetailsSummaryId: string = '',
    caseId: string = ''
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `getCapitalGainRealStateTable/` + incomeDetailsSummaryId + "/" + caseId;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  get210AssetSubtypeOtherCapitalGainTable(
    incomeDetailsSummaryId: string = '',
    caseId: string = ''
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `getOtherCapitalGainTable/` + incomeDetailsSummaryId + "/" + caseId;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  get210AssetSubtypeDonationsTable(
    serviceToEmployeeId: string = '',
    incomeDetailsSummaryId: string = '',
    caseId: string = ''
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `getDonationsTable/` + serviceToEmployeeId + '/' + incomeDetailsSummaryId + "/" + caseId;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  get210CapitalGainRealStateTable(
    incomeDetailsSummaryId: string = '',
    caseId: string = ''
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `getCapitalGainRealStateTable/` + incomeDetailsSummaryId + "/" + caseId;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  /******************* MODEL CAPITAL GAIN REAL STATE EXPENSES ********/

  getModelCapitalGainRealStateExpenses(
    modelDetailedResultId: string = '',
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-capital-gain-real-state-expenses/` + modelDetailedResultId

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  updateModelCapitalGainRealStateExpenses(
    id: string = '',
    item: any,
  ): Observable<any> {
    const body = JSON.stringify(item);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-capital-gain-real-state-expenses/${id}`;

    return this.http.put(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  /*********** MODEL 151 PAYER CAPITAL GAIN REAL STATE ***********/

  getModel151PayerCapitalGainRealState(
    model151CategoryId: string = '',
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-151-payer-capital-gain-real-state/` + model151CategoryId;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  createModel151PayerCapitalGainRealState(
    item: any,
  ): Observable<any> {
    const body = JSON.stringify(item);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-151-payer-capital-gain-real-state`;

    return this.http.post(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  updateModel151PayerCapitalGainRealState(
    itemId: string = '',
    item: any,
  ): Observable<any> {
    const body = JSON.stringify(item);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-151-payer-capital-gain-real-state/` + itemId;

    return this.http.put(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  deleteModel151PayerCapitalGainRealState(
    itemId: string = '',
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-151-payer-capital-gain-real-state/` + itemId;

    return this.http.delete(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res),
        catchError(err => {
          console.error('Error: ' + err.error.code + ': ' +
            err.error.message + ' (' + err.status + ': ' +
            err.statusText + ')'
          );
          return err;
        })
      );
  }

  /******************** 151 ********************/

  get151AssetSubtypeRentingHousesTable(
    incomeDetailsSummaryId: string = '',
    tableName: string = ''
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `getRentingHousesTable151/` + incomeDetailsSummaryId + '/table/' + tableName;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  get151AssetSubtypeIRHousesTable(
    incomeDetailsSummaryId: string = '',
    tableName: string = ''
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `getIRHousesTable151/` + incomeDetailsSummaryId + '/table/' + tableName;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  get151AssetSubtypeEconomicActivityTable(
    incomeDetailsSummaryId: string = '',
    tableName: string = ''
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `getEconomicActivityTable151/` + incomeDetailsSummaryId + '/table/' + tableName;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  get151AssetSubtypeDividendsTable(
    incomeDetailsSummaryId: string = '',
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `getDividendsTable151/` + incomeDetailsSummaryId;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  get151AssetSubtypeInterestsTable(
    incomeDetailsSummaryId: string = '',
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `getInterestsTable151/` + incomeDetailsSummaryId;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  get151AssetSubtypeCanonsTable(
    incomeDetailsSummaryId: string = '',
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `getCanonsTable151/` + incomeDetailsSummaryId;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  get151AssetSubtypeWorkPerformanceTable(
    incomeDetailsSummaryId: string = '',
    tableName: string = ''
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `getWorkPerformanceTable151/` + incomeDetailsSummaryId + '/table/' + tableName;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  get151AssetSubtypePensionPlansTable(
    incomeDetailsSummaryId: string = '',
    tableName: string = ''
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `getPensionPlansTable151/` + incomeDetailsSummaryId + '/table/' + tableName;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  get151AssetSubtypeCapitalGainStocksTable(
    incomeDetailsSummaryId: string = '',
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `getCapitalGainStocksTable151/` + incomeDetailsSummaryId;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  get151AssetSubtypeCapitalGainInvestmentFundsTable(
    incomeDetailsSummaryId: string = '',
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `getCapitalGainInvestmentFundsTable151/` + incomeDetailsSummaryId;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  get151AssetSubtypeCapitalGainRealStateTable(
    incomeDetailsSummaryId: string = '',
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `getCapitalGainRealStateTable151/` + incomeDetailsSummaryId;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  get151AssetSubtypeOtherCapitalGainTable(
    incomeDetailsSummaryId: string = '',
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `getOtherCapitalGainTable151/` + incomeDetailsSummaryId;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  get151AssetSubtypeDonationsTable(
    serviceToEmployeeId: string = '',
    incomeDetailsSummaryId: string = '',
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `getDonationsTable151/` + serviceToEmployeeId + '/' + incomeDetailsSummaryId;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  get151AssetSubtypeOtherTable(
    incomeDetailsSummaryId: string = '',
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `getOtherTable151/` + incomeDetailsSummaryId;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }


  /******************* GENERATE 210 *****************/
  generate210form(
    item: any,
    caseId: string,
    serviceToEmployeeId: string
  ): Observable<any> {
    const payload = buildGenerateFormsPayload(item);
    const body = JSON.stringify(payload);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `${serviceToEmployeeId}/${caseId}/generate-txt`;

    return this.http.post(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  generate210formDraftPdf(
    item: any,
    serviceToEmployeeId: string
  ): Observable<any> {
    const payload = buildGenerateFormsDraftPdfPayload(item);
    const body = JSON.stringify(payload);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `${serviceToEmployeeId}/generate-draft-pdf`;

    return this.http.post(requesttUrl, body, { headers: headers, responseType: 'blob' })
  }

  generate210formDraftPdfBase64(item: GenerateDraftPdfInput, serviceToEmployeeId: string): Observable<IPdfBase64ResponseType> {
    const body: GenerateFormsDraftPdfPayload = {
      mod: item.mod,
      ejf: item.ejf !== undefined ? String(item.ejf) : undefined,
      fic: item.fic,
      idi: item.idi,
    };

    const requesttUrl = this.apiUrl + `${serviceToEmployeeId}/generate-draft-pdf`;
    return this.http.post<IPdfBase64Response>(requesttUrl, body)
      .pipe(
        take(1),
        map(res => res.respuesta)
      );
  }

  updateModelPreparation(
    id: string = '',
    item: any,
  ): Observable<any> {
    const payload = buildModelPreparationPayload(item);
    const body = JSON.stringify(payload);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-preparation-210/${id}`;

    return this.http.patch(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  /******************* MODEL PREPARATION 210 *****************/

  getModelPreparationByModelDetailedResultId(
    modelDetailedResultId: string = '',
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-preparation-210/` + modelDetailedResultId;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  /******************* MODEL PREPARATION 030 *****************/

  getModel030PreparationByCaseId(
    caseId: string = '',
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-preparation-030/` + caseId;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }


  getModel149PreparationByCaseId(
    caseId: string = '',
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-preparation-149/` + caseId;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }






  /******************* GENERATE FILE TO UPLOAD *****************/


  getFilesUploadEyg1ByCaseId(
    caseId: string = '',
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `files-upload-eyg1/` + caseId;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  createFileUploadEyg1(
    item: any,
  ): Observable<any> {
    const body = JSON.stringify(item);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `files-upload-eyg1`;

    return this.http.post(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  createBotAction(
    item: any,
  ): Observable<any> {
    const body = JSON.stringify(item);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `bot-actions`;

    return this.http.post(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }




  /******************* GENERATE 151 *****************/
  generate151form(
    caseId: string,
    serviceToEmployeeId: string
  ): Observable<any> {
    // const body = JSON.stringify(item);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `${serviceToEmployeeId}/${caseId}/generate-txt-151/`;

    return this.http.post(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  generate151formDraftPdf(
    item: any,
    serviceToEmployeeId: string
  ): Observable<any> {
    const payload = buildGenerateForms151DraftPdfPayload(item);
    const body = JSON.stringify(payload);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `${serviceToEmployeeId}/generate-draft-151-pdf`;

    return this.http.post(requesttUrl, body, { headers: headers, responseType: 'blob' })
  }

  generate151formDraftPdfBase64(item: GenerateDraftPdfInput, serviceToEmployeeId: string): Observable<IPdfBase64ResponseType> {
    const body: GenerateFormsDraftPdfPayload = {
      mod: item.mod,
      ejf: item.ejf !== undefined ? String(item.ejf) : undefined,
      fic: item.fic,
      idi: item.idi,
    };

    const requesttUrl = this.apiUrl + `${serviceToEmployeeId}/generate-draft-151-pdf`;
    return this.http.post<IPdfBase64Response>(requesttUrl, body)
      .pipe(
        take(1),
        map(res => res.respuesta)
      );
  }

  updateModelPreparation151(
    id: string = '',
    item: any,
  ): Observable<any> {
    const payload = buildModelPreparation151Payload(item);
    const body = JSON.stringify(payload);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-preparation-151/${id}`;

    return this.http.patch(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  /******************* GENERATE 030 *****************/

  generate030form(
    item: any,
    serviceToEmployeeId: string,
    caseId: string
  ): Observable<any> {
    const payload = buildGenerateForms030Payload(item);
    const body = JSON.stringify(payload);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `${serviceToEmployeeId}/${caseId}/generate-txt-030`;

    return this.http.post(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  generate030formDraftPdf(
    item: any,
    serviceToEmployeeId: string
  ): Observable<any> {
    const payload = buildGenerateFormsDraftPdfPayload(item);
    const body = JSON.stringify(payload);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `${serviceToEmployeeId}/generate-draft-030-pdf`;

    return this.http.post(requesttUrl, body, { headers: headers, responseType: 'blob' })
  }

  updateModelPreparation030(
    id: string = '',
    item: any,
  ): Observable<any> {
    const payload = buildModelPreparation030Payload(item);
    const body = JSON.stringify(payload);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-preparation-030/${id}`;

    return this.http.patch(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  /******************* SUBMIT TAX FORM *****************/

  submitTaxForm(
    item: any,
  ): Observable<any> {
    const body = JSON.stringify(item);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `aeat/submit-tax-form`;

    return this.http.post(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  /******************* MODEL DETAILED RESULT *****************/

  getModelDetailResult(
    modelDetailedResultId: string
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-detailed-result/${modelDetailedResultId}`;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  getModelDetailResultsByEmployeeId(
    employeeId: string,
    caseId: string
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-detailed-result/employee/${employeeId}/${caseId}`;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  getModelDetailResultsByEmployeeIdDependant(
    employeeId: string,
    year: string
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-detailed-result/dependant/${employeeId}/${year}`;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  getModelDetailResults151ByEmployeeIdDependant(
    employeeId: string,
    year: string
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-detailed-result-151/dependant/${employeeId}/${year}`;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  updateModelDetailedResult(
    id: string = '',
    item: any,
  ): Observable<any> {
    const payload = buildModelDetailedResultPayload(item);
    const body = JSON.stringify(payload);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-detailed-result/${id}`;

    return this.http.patch(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  cloneModelDetailedResult(
    id: string = '',
    item: any,
  ): Observable<any> {
    const body = JSON.stringify(item);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-detailed-result/${id}/clonemodel`;

    return this.http.patch(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  createModelDetailedResult(
    item: any,
  ): Observable<any> {
    const payload = buildModelDetailedResultPayload(item);
    const body = JSON.stringify(payload);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-detailed-result`;

    return this.http.post(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  deleteModelDetailedResult(
    itemId: string = '',
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-detailed-result/` + itemId

    return this.http.delete(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res),
        catchError(err => {
          console.error('Error: ' + err.error.code + ': ' +
            err.error.message + ' (' + err.status + ': ' +
            err.statusText + ')'
          );
          return err;
        })
      );
  }

  /******************* MODEL DETAILED RESULT 151 *****************/

  getModelDetailResults151ByCaseId(
    caseId: string
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-detailed-result-151/case/${caseId}`;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  updateModelDetailedResult151(
    id: string = '',
    item: any,
  ): Observable<any> {
    const payload = buildModelDetailedResult151Payload(item);
    const body = JSON.stringify(payload);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-detailed-result-151/${id}`;

    return this.http.patch(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  /******************* PERIODS MODEL DETAILED RESULT *****************/

  getPeriodsModelResult(
    modelDetailedResultId: string
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-period-dates/${modelDetailedResultId}`;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  createPeriodsModelResult(
    item: any,
  ): Observable<any> {
    const payload = buildModelPeriodDatesPayload(item);
    const body = JSON.stringify(payload);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-period-dates`

    return this.http.post(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  updatePeriodsModelResult(
    id: string = '',
    item: any,
  ): Observable<any> {
    const payload = buildModelPeriodDatesPayload(item);
    const body = JSON.stringify(payload);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-period-dates/${id}`;

    return this.http.put(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  deletePeriodsModelResult(
    itemId: string = '',
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-period-dates/` + itemId

    return this.http.delete(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res),
        catchError(err => {
          console.error('Error: ' + err.error.code + ': ' +
            err.error.message + ' (' + err.status + ': ' +
            err.statusText + ')'
          );
          return err;
        })
      );
  }

  /******************* PERIODS TYPES MODEL DETAILED RESULT *****************/

  getPeriodTypesModelResult(
    modelDetailedResultId: string
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-period-types/${modelDetailedResultId}`;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  createPeriodTypesModelResult(
    item: any,
  ): Observable<any> {
    const payload = buildModelPeriodTypesPayload(item);
    const body = JSON.stringify(payload);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-period-types`

    return this.http.post(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  updatePeriodTypesModelResult(
    id: string = '',
    item: any,
  ): Observable<any> {
    const payload = buildModelPeriodTypesPayload(item);
    const body = JSON.stringify(payload);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-period-types/${id}`;

    return this.http.put(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  deletePeriodTypesModelResult(
    itemId: string = '',
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-period-types/` + itemId

    return this.http.delete(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res),
        catchError(err => {
          console.error('Error: ' + err.error.code + ': ' +
            err.error.message + ' (' + err.status + ': ' +
            err.statusText + ')'
          );
          return err;
        })
      );
  }

  /******************* EXPENSES MODEL DETAILED RESULT *****************/

  getModelResultExpenses(
    modelDetailedResultId: string
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-detailed-result-expenses/${modelDetailedResultId}`;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  createModelResultExpenses(
    item: any,
  ): Observable<any> {
    const payload = buildModelDetailedResultExpensesPayload(item);
    const body = JSON.stringify(payload);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-detailed-result-expenses`

    return this.http.post(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  updateModelResultExpenses(
    id: string = '',
    item: any,
  ): Observable<any> {
    const payload = buildModelDetailedResultExpensesPayload(item);
    const body = JSON.stringify(payload);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-detailed-result-expenses/${id}`;

    return this.http.put(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  deleteModelResultExpenses(
    itemId: string = '',
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-detailed-result-expenses/` + itemId

    return this.http.delete(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res),
        catchError(err => {
          console.error('Error: ' + err.error.code + ': ' +
            err.error.message + ' (' + err.status + ': ' +
            err.statusText + ')'
          );
          return err;
        })
      );
  }

  /******************* MODEL RESULT DONATION *****************/

  getModelResultDonation(
    modelDetailedResultId: string
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-result-donation/${modelDetailedResultId}`;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  createModelResultDonation(
    item: any,
  ): Observable<any> {
    const payload = buildModelResultDonationPayload(item);
    const body = JSON.stringify(payload);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-result-donation`

    return this.http.post(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  updateModelResultDonation(
    id: string = '',
    item: any,
  ): Observable<any> {
    const payload = buildModelResultDonationPayload(item);
    const body = JSON.stringify(payload);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-result-donation/${id}`;

    return this.http.put(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  deleteModelResultDonation(
    itemId: string = '',
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-result-donation/` + itemId

    return this.http.delete(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res),
        catchError(err => {
          console.error('Error: ' + err.error.code + ': ' +
            err.error.message + ' (' + err.status + ': ' +
            err.statusText + ')'
          );
          return err;
        })
      );
  }

  /******************* PAYER CAPITAL GAIN REAL STATE *****************/

  getModelPayerCapitalGainRealState(
    modelDetailedResultId: string
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-payer-capital-gain-real-state/${modelDetailedResultId}`;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  createModelPayerCapitalGainRealState(
    item: any,
  ): Observable<any> {
    const payload = buildModelPayerCapitalGainRealStatePayload(item);
    const body = JSON.stringify(payload);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-payer-capital-gain-real-state`;

    return this.http.post(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  updateModelPayerCapitalGainRealState(
    id: string = '',
    item: any,
  ): Observable<any> {
    const payload = buildModelPayerCapitalGainRealStatePayload(item);
    const body = JSON.stringify(payload);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-payer-capital-gain-real-state/${id}`;

    return this.http.put(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  deleteModelPayerCapitalGainRealState(
    itemId: string = '',
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-payer-capital-gain-real-state/` + itemId

    return this.http.delete(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res),
        catchError(err => {
          console.error('Error: ' + err.error.code + ': ' +
            err.error.message + ' (' + err.status + ': ' +
            err.statusText + ')'
          );
          return err;
        })
      );
  }

  /******************* TENANTS MODEL DETAILED RESULT *****************/

  createTenantsModelResult(
    item: any,
  ): Observable<any> {
    const body = JSON.stringify(item);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `rental-income-tenants`

    return this.http.post(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  updateTenantsModelResult(
    id: string = '',
    item: any,
  ): Observable<any> {
    const body = JSON.stringify(item);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `rental-income-tenants/${id}`;

    return this.http.put(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  deleteTenantsModelResult(
    itemId: string = '',
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `rental-income-tenants/` + itemId

    return this.http.delete(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res),
        catchError(err => {
          console.error('Error: ' + err.error.code + ': ' +
            err.error.message + ' (' + err.status + ': ' +
            err.statusText + ')'
          );
          return err;
        })
      );
  }


  /******************* DONATIONS MODEL DETAILED RESULT *****************/

  getDonationModelResult(
    modelDetailedResultId: string
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `donation-model-detailed-result/${modelDetailedResultId}`;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  /******************* DONATIONS MODEL DETAILED RESULT 151 *****************/

  getDonationModelResult151(
    modelDetailedResultId: string
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `donation-model-detailed-result-151/${modelDetailedResultId}`;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }



  /******************* MODEL 151 CATEGORY *****************/

  getModel151Categories(
    model151CategoriesId: string
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-151-categories/${model151CategoriesId}`;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  updateModel151Categories(
    id: string = '',
    item: any,
  ): Observable<any> {
    const body = JSON.stringify(item);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-151-categories/${id}`;

    return this.http.patch(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  createModel151Categories(
    item: any,
  ): Observable<any> {
    const body = JSON.stringify(item);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-151-categories`;

    return this.http.post(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  deleteModel151Categories(
    itemId: string = '',
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-151-categories/` + itemId

    return this.http.delete(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res),
        catchError(err => {
          console.error('Error: ' + err.error.code + ': ' +
            err.error.message + ' (' + err.status + ': ' +
            err.statusText + ')'
          );
          return err;
        })
      );
  }

  /******************* PERIODS MODEL 151 CATEGORIES *****************/

  getPeriodsModel151Categories(
    model151CategoriesId: string
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-151-period-dates/${model151CategoriesId}`;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  createPeriodsModel151Categories(
    item: any,
  ): Observable<any> {
    const payload = buildModelPeriodDates151Payload(item);
    const body = JSON.stringify(payload);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-151-period-dates`

    return this.http.post(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  updatePeriodsModel151Categories(
    id: string = '',
    item: any,
  ): Observable<any> {
    const payload = buildModelPeriodDates151Payload(item);
    const body = JSON.stringify(payload);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-151-period-dates/${id}`;

    return this.http.put(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  deletePeriodsModel151Categories(
    itemId: string = '',
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-151-period-dates/` + itemId

    return this.http.delete(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res),
        catchError(err => {
          console.error('Error: ' + err.error.code + ': ' +
            err.error.message + ' (' + err.status + ': ' +
            err.statusText + ')'
          );
          return err;
        })
      );
  }

  getBinaryPdfFileByCSV(csv: string) {
    return lastValueFrom(this.http.get(`${this.apiUrl}tax-returns-engine/generate/pdf/${csv}`, { responseType: 'arraybuffer' }));
  }

  getBinaryPdfFileByCSVNoCheck(csv: string, env: 'production' | 'preproduction') {
    return lastValueFrom(this.http.get(`${this.apiUrl}tax-returns-engine/generate/pdf-no-check/env/${env}/csv/${csv}`, { responseType: 'arraybuffer' }));
  }

  /******************* GENERATE 149 *****************/

  generate149form(
    item: any,
    serviceToEmployeeId: string,
    caseId: string
  ): Observable<any> {
    const payload = buildGenerateForms149Payload(item);
    const body = JSON.stringify(payload);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `${serviceToEmployeeId}/${caseId}/generate-txt-149`;

    return this.http.post(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  generate149formDraftPdf(
    item: any,
    serviceToEmployeeId: string
  ): Observable<any> {
    const payload = buildGenerateFormsDraftPdfPayload(item);
    const body = JSON.stringify(payload);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `${serviceToEmployeeId}/generate-draft-149-pdf`;

    return this.http.post(requesttUrl, body, { headers: headers, responseType: 'blob' })
  }

  generateExcelTemplate149(caseId: string): Observable<Blob> {
    const requesttUrl = this.apiUrl + `${caseId}/generate-excel-template-149`;
    return this.http.post(requesttUrl, {}, { responseType: 'blob' });
  }

  importExcelTemplate149(caseId: string, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    const requesttUrl = this.apiUrl + `${caseId}/import-excel-template-149`;
    return this.http.post(requesttUrl, formData);
  }

  updateModelPreparation149(
    id: string = '',
    item: any,
  ): Observable<any> {
    const payload = buildModelPreparation149Payload(item);
    const body = JSON.stringify(payload);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-preparation-149/${id}`;

    return this.http.patch(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  processDocs149(item: File[], serviceToEmployee: string, caseId: string, contactData) {
    const formData: FormData = new FormData();
    // Append each file individually to the FormData
    item.forEach((file: File) => formData.append('file', file, file.name));

    formData.append('contactEmail', contactData.contactEmail)
    formData.append('contactPhone', contactData.contactPhone)

    const url = this.apiUrl + `${serviceToEmployee}/${caseId}/upload-docs-149`;

    return this.http.post(url, formData)
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  processTaxRefund210(item: File[], serviceToEmployee: string, modelPreparationId: string, contactData) {
    const formData: FormData = new FormData();
    // Append each file individually to the FormData
    item.forEach((file: File) => formData.append('file', file, file.name));

    formData.append('contactEmail', contactData.contactEmail)
    formData.append('contactPhone', contactData.contactPhone)

    const url = this.apiUrl + `${serviceToEmployee}/${modelPreparationId}/process-tax-refund-210`;

    return this.http.post(url, formData)
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          return throwError(err);
        })
      );
  }

  getTaxRefund210Receipt(serviceToEmployee: string, modelPreparationId: string): Observable<Blob> {
    const url = this.apiUrl + `service-to-employee/${serviceToEmployee}/aeat-210-presentation/${modelPreparationId}/receipt`;
    return this.http.get(url, { responseType: 'blob' });
  }

  getMainTaxPayerRelatedToThisEmployee(employeeId: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}model-preparation-149/employee/${employeeId}/main-taxpayers-list`);
  }

  getRefNumber(caseId: string): Observable<{ refNumber: string }> {
    return this.http.get<{ refNumber: string }>(`${this.apiUrl}model-preparation-149/case/${caseId}/ref-number`);
  }

  updateCaseDeadline(serviceToEmployee, caseId, caseDeadline) {
    const body = JSON.stringify({ caseDeadline });
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `service-to-employee/${serviceToEmployee}/case/${caseId}/update-deadline`;

    return this.http.patch(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

}
